import { Injectable, OnDestroy } from '@angular/core';
import amplitude from 'amplitude-js';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { TitleService } from './title.service';
import { CompanyService } from './company.service';
import * as Sentry from '@sentry/angular';
import { User } from '../shared/model/user';
import { UserService } from './user.service';
import { SessionService } from '../core/session.service';
import { ConsentService } from './consent.service';
import { differenceInSeconds } from 'date-fns';

export enum EVENT {
  APP_INIT = 'App Init',
  PAGE_VIEW = 'Pageview',
  SAVE_SICK_NOTE = 'Save Sick Note Click',
  NEW_PERSON = 'New Person',
  ERROR = 'Error',
  DASHBOARD_CHANGED = 'Dashboard Changed',
  LOCATION_CHANGED = 'Location Changed',
  IMPORT_CLICK = 'Import Click',
  EXPORT_CLICK = 'Export Click',
  FILTER_CLICK = 'Filter Click',
  LOGIN_CLICK = 'Login Click',
  EDIT_CLICK = 'Edit Click',
  PAGE_CLOSE = 'Page Close',
  DOCUMENT_APPROVE = 'Document Approve',
  DUCUMENT_REPROVE = 'Document Reprove',
  FINANCIAL_PANEL_ACCESS = 'Financial Panel Access',
  FINANCIAL_PANEL_SUPPORT_CLICK = 'Financial Panel Support Click',
  RULE_JSON_IMPORT_OPENED = 'Rule JSON Import Opened',
  RULE_JSON_IMPORT_CONFIRMED = 'Rule JSON Import Confirmed',
  RULE_JSON_IMPORT_FAILED = 'Rule JSON Import Failed',
  RULE_JSON_EXPORT_COPIED = 'Rule JSON Export Copied',
  RULE_JSON_EXPORT_FAILED = 'Rule JSON Export Failed',
  RULE_JSON_BULK_IMPORT_DONE = 'Rule JSON Bulk Import Done',
  RULE_JSON_BULK_EXPORT_COPIED = 'Rule JSON Bulk Export Copied',
  RULE_JSON_PARTIAL_IMPORT_DETECTED = 'Rule JSON Partial Import Detected',
  RULE_JSON_PARTIAL_COMPLETED = 'Rule JSON Partial Completed',
  RULE_JSON_PARTIAL_SKIPPED = 'Rule JSON Partial Skipped',
  VERIFY_BLOCKED = 'Verify Blocked',
  SW_VERSION_READY = 'SW Version Ready',
  SW_UNRECOVERABLE = 'SW Unrecoverable',
  // GA4 names an event with letters, digits and underscores only; the names
  // above predate this one and do not follow that rule.
  VERIFICATION_ANSWERED = 'verification_answered',
  EMPLOYEE_MESSAGE_OVER_LIMIT = 'employee_message_over_limit',
}

export enum PROPERTY {
  PAGE_TITLE = 'page_title',
  PAGE_URL = 'page_path',
}

export enum WHERE {
  SICK_NOTES_LIST = 'SickNotesList',
  DASHBOARD = 'DashboardComponent',
  SICK_NOTES_DIALOG = 'SickNotesAnalyzeDialog',
  DOCUMENTS_DIALOG = 'DocumentsDetailsDialog',
  DOCUMENTS_EDIT_DIALOG = 'DocumentsEditDialog',
  DIGITALIZE_SICK_NOTES = 'DigitalizeSickNoteDialog',
  SICK_NOTES_EDIT = 'SickNoteEditDialog',
  OCCUPACIONAL_LIST = 'OccupacionalEditDialog',
  REFUND_LIST = 'RefundEditDialog',
  FINANCIAL_PANEL = 'FinancialPanel',
  RULES_LIST = 'RulesList',
}

export enum TYPECLOSURE {
  APPROVE = 'Approve',
  CLOSED_MANUALLY = 'Manual',
  BEFOREUNLOAD = 'Beforeunload',
  REFUSE = 'Refuse',
  CLOSE_WINDOW = 'Window Closed',
}

@Injectable({
  providedIn: 'root',
})
export class Analytics implements OnDestroy {
  idsRegExp =
    /^\d+$|^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  private consentSub: Subscription | null = null;
  private lastUser: User | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private ga: AngularFireAnalytics,
    private title: TitleService,
    private companyService: CompanyService,
    private session: SessionService,
    private userService: UserService,
    private consent: ConsentService
  ) {}

  get amplitude() {
    return amplitude.getInstance();
  }

  startTracking() {
    if (this.consentSub) return;

    // The analytics SDKs (Amplitude, Firebase Analytics) read/write cookies and
    // storage during init; a browser that blocks site data can make them throw.
    // Analytics is optional, so keep the failure contained here instead of
    // letting it bubble to app bootstrap (an uncaught throw blanks the app).
    try {
      // Amplitude starts opted-out; the consent subscription below flips it
      // on/off as the user toggles the "analytics" category. Same idea for
      // Firebase Analytics via setAnalyticsCollectionEnabled. With amplitude-js
      // v8, setOptOut(true) discards events rather than queueing them, so
      // toggling does not leak buffered data on re-opt-in.
      const amplitudeConfig: amplitude.Config = {
        logLevel: environment.production ? 'ERROR' : 'WARN',
        optOut: true,
      };
      this.amplitude.init(
        environment.amplitudeApiKey,
        undefined,
        amplitudeConfig
      );
      this.amplitude.setVersionName(environment.version);
      this.ga.setAnalyticsCollectionEnabled(false);

      this.consentSub = this.consent
        .category$('analytics')
        .pipe(takeUntil(this.destroy$))
        .subscribe((granted) => {
          // setOptOut first: setUserProperties emits an identify event, and
          // amplitude-js v8 discards those while optOut is true rather than
          // queueing them.
          this.amplitude.setOptOut(!granted);
          this.ga.setAnalyticsCollectionEnabled(granted);
          // loggedInUser$ is shareReplay(1) and does not re-emit when consent
          // changes, so someone who logs in before accepting would otherwise
          // never be identified: no user id, no properties, for the session.
          if (granted) this.updateUser(this.lastUser);
        });

      this.session.loggedInUser$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (user) => {
          this.lastUser = user;
          this.updateUser(user);
        },
        error: (error) => Sentry.captureMessage(error.message),
      });
    } catch (error) {
      Sentry.captureException(error);
      if (!environment.production) {
        console.error('Analytics init failed', error);
      }
    }
  }

  ngOnDestroy(): void {
    // Safe today because providedIn: 'root' (never destroyed in practice).
    // Kept as defensive insurance if this is ever scoped to a feature
    // module: without it the session + consent subscriptions would leak.
    this.destroy$.next();
    this.destroy$.complete();
    this.consentSub?.unsubscribe();
    this.consentSub = null;
  }

  pageView(url: string) {
    if (!this.consent.has('analytics')) return;
    const clearUrl = this.clearUrl(url),
      properties: any = {};
    properties[PROPERTY.PAGE_URL] = clearUrl;
    properties[PROPERTY.PAGE_TITLE] = this.title.getTitle();
    properties.company_id = this.session.currentCompanyId;
    this.amplitude.logEvent(EVENT.PAGE_VIEW, properties);
    this.ga.logEvent('screen_view', properties).then();
  }

  newPerson(search: string) {
    this.event(EVENT.NEW_PERSON, { search });
  }

  error(where: WHERE | string, what: string) {
    this.event(EVENT.ERROR, null, where, what);
  }

  updateUser(user: User | null) {
    if (!this.consent.has('analytics')) return;
    if (user) {
      this.amplitude.setUserId(user.id);
      this.amplitude.setUserProperties({
        email: user.email,
        name: user.name,
      });
    }
  }

  event(
    action: EVENT,
    properties?: any,
    where?: WHERE | string,
    what?: string
  ) {
    if (!this.consent.has('analytics')) return;
    if (!properties) {
      properties = {};
    }
    properties.where = where;
    properties.what = what;
    properties.company_id = this.session.currentCompanyId;
    this.amplitude.logEvent(action, properties);
    this.ga.logEvent(action, properties).then();
  }

  private clearUrl(url: string): string {
    return url
      .replace(/c\/[a-zA-Z0-9]+(\/.*)?/g, 'c/companyId$1')
      .split('/')
      .map((part) => part.split('?')[0])
      .map((part) => part.split('#')[0])
      .filter((part) => !part.match(this.idsRegExp))
      .join('/');
  }

  // Neither this nor `pageClosed` repeats the operator's e-mail on the event:
  // `updateUser` already sets it as an Amplitude user property, where it stays
  // segmentable, purgeable in one place, and out of every event payload.
  trackingPageViewTime(
    where?: WHERE,
    openTime?: Date,
    closeTime?: Date,
    event?: EVENT,
    extras?: Record<string, unknown>
  ) {
    if (!this.consent.has('analytics')) return;
    const timeOnScreen = differenceInSeconds(closeTime, openTime),
      properties: any = { ...extras },
      action = event;
    properties.company_id = this.session.currentCompanyId;
    properties.where = where;
    properties.timeOnScreen = timeOnScreen;
    this.amplitude.logEvent(action, properties);
  }

  pageClosed(
    where?: WHERE,
    openTime?: Date,
    closeTime?: Date,
    type?: TYPECLOSURE
  ) {
    if (!this.consent.has('analytics')) return;
    const timeOnScreen = differenceInSeconds(closeTime, openTime),
      properties: any = {},
      action = EVENT.PAGE_CLOSE;
    properties.company_id = this.session.currentCompanyId;
    properties.where = where;
    properties.timeOnScreen = timeOnScreen;
    properties.type = type;
    this.amplitude.logEvent(action, properties);
  }
}
